import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Star, Archive, Trash2 } from 'lucide-react';
import { getRelativeTime } from '../utils/dateFormatter';

/**
 * Displays a single conversation item in the list with improved contrast and functionality
 */
const ConversationItem = React.memo(({ 
  conversation, 
  isSelected, 
  onClick,
  onDelete,
  onArchive,
  onToggleFavorite,
  isFavorite = false
}) => {
  const [showActions, setShowActions] = useState(false);
  
  // Format the message count for display
  const messageCount = conversation.metadata?.messageCount || 0;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="relative"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <button
        onClick={onClick}
        className={`w-full p-3 rounded-md text-left transition-colors ${
          isSelected
            ? 'bg-emerald-700/40 border-l-2 border-emerald-500 pl-3'
            : 'bg-slate-700/40 hover:bg-slate-700/70 border-l-2 border-transparent hover:border-emerald-600/50 pl-3'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Title with favorite indicator */}
            <div className="flex items-center gap-1.5">
              <h3 className="font-medium truncate text-white">
                {conversation.metadata?.title || 'Untitled Chat'}
              </h3>
              {isFavorite && (
                <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 flex-shrink-0" />
              )}
            </div>
            
            {/* Preview with improved contrast */}
            <p className="text-xs truncate text-slate-200 mt-1 leading-relaxed">
              {conversation.lastMessage || 'No messages yet'}
            </p>
          </div>
          <time className="text-xs whitespace-nowrap text-emerald-300 mt-0.5">
            {getRelativeTime(conversation.metadata?.updatedAt)}
          </time>
        </div>
        
        {/* Message count badge with better contrast */}
        <div className="flex items-center mt-2 justify-between">
          <span className={`px-2 py-1 text-xs rounded-md flex items-center gap-1.5 ${
            isSelected 
              ? 'bg-emerald-500/20 text-emerald-200' 
              : 'bg-slate-600 text-slate-200'
          }`}>
            <MessageSquare className="w-3 h-3" />
            {messageCount} {messageCount === 1 ? 'message' : 'messages'}
          </span>
          
          {isSelected && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-1"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '0.3s' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '0.6s' }} />
            </motion.div>
          )}
        </div>
      </button>

      {/* Quick action buttons with improved positioning and functionality */}
      {showActions && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute top-2 right-2 flex space-x-1 z-10 bg-slate-800/90 backdrop-blur-sm rounded-md p-0.5 shadow-lg"
        >
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(conversation);
            }}
            className={`p-1.5 rounded-md ${
              isFavorite 
                ? 'bg-yellow-600/30 text-yellow-400' 
                : 'hover:bg-slate-700 text-slate-300 hover:text-yellow-400'
            } transition-colors`}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            <Star className={`w-4 h-4 ${isFavorite ? 'fill-yellow-400' : ''}`} />
          </button>
          
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onArchive(conversation);
            }}
            className="p-1.5 rounded-md hover:bg-slate-700 text-slate-300 hover:text-emerald-300 transition-colors"
            title="Archive conversation"
          >
            <Archive size={14} />
          </button>
          
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onDelete(conversation);
            }}
            className="p-1.5 rounded-md hover:bg-red-900/30 text-slate-300 hover:text-red-300 transition-colors"
            title="Delete conversation"
          >
            <Trash2 size={14} />
          </button>
        </motion.div>
      )}
    </motion.div>
  );
});

export default ConversationItem;